import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserCog, Eye, FileText } from 'lucide-react'
import {
  useGetBankDetailFixups,
  useGetIdentityFixups,
  useGetOnboardingFixups,
  useUpdatePersonIdentity,
  useCheckIdConflict,
  useMergePeople,
} from '../hooks/backend/interviews'
import { useAccess } from '../hooks/useAccess'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Button } from '../lib/shadcn/button'
import { Badge } from '../lib/shadcn/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { toast } from '../lib/shadcn/sonner'

type FixupRow = {
  person_id: string
  first_name: string
  last_name: string
  role: string
  id_number: string | null
  date_of_birth: string | null
  id_status: string
}

type Draft = { idNumber: string; dob: string }

type OnboardingRow = {
  id: string
  sheet_name: string
  sheet_id: string
  sheet_email: string
  sheet_phone: string
  submitted_at_raw: string | null
  matched_application_id: string | null
  matched_person_id: string | null
  match_basis: string | null
  matched: boolean
  db_name: string | null
  db_id: string | null
  db_email: string | null
  pipeline_status: string | null
  id_length_flag: boolean
  id_mismatch_flag: boolean
}

function statusVariant(status: string): 'destructive' | 'warning' {
  return status === 'missing' ? 'destructive' : 'warning'
}

function humanisePipeline(code: string | null): string {
  if (!code) return '\u2014'
  const spaced = code.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export default function DataFixes() {
  const { loading: accessLoading, canAct } = useAccess()
  const { caption } = useCapabilities()
  const [view, setView] = useState<'ids' | 'onboarding' | 'banking'>('ids')
  const fixups = useGetIdentityFixups()
  const onboarding = useGetOnboardingFixups()
  const banking = useGetBankDetailFixups()
  const [bankingOnlyWorking, setBankingOnlyWorking] = useState(false)
  const [onboardingFilter, setOnboardingFilter] = useState('')
  const update = useUpdatePersonIdentity()
  const checkConflict = useCheckIdConflict()
  const merge = useMergePeople()
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [mergePrompt, setMergePrompt] = useState<{
    row: FixupRow
    conflictPersonId: string
    fullName: string
  } | null>(null)
  const [merging, setMerging] = useState(false)

  const trigger = fixups.trigger
  const load = useCallback(() => {
    trigger({}, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    load()
  }, [load])

  const rows = (fixups.data as FixupRow[] | undefined) ?? []

  // Reset drafts to server values whenever the list changes (initial load + refetch).
  useEffect(() => {
    const next: Record<string, Draft> = {}
    for (const r of rows) {
      next[r.person_id] = {
        idNumber: r.id_number ?? '',
        dob: r.date_of_birth ?? '',
      }
    }
    setDrafts(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixups.data])

  const doUpdate = useCallback(
    async (row: FixupRow, draft: Draft) => {
      await update
        .trigger({
          person_id: row.person_id,
          id_number: draft.idNumber.trim() || null,
          date_of_birth: draft.dob.trim() || null,
        })
        .result
      toast.success('Identity updated', {
        description: `${row.first_name} ${row.last_name}`,
      })
      load()
    },
    [update, load],
  )

  const handleSave = useCallback(
    async (row: FixupRow) => {
      const draft = drafts[row.person_id]
      if (!draft) return
      setSavingId(row.person_id)
      try {
        const digits = draft.idNumber.replace(/\D/g, '')
        if (digits.length === 13) {
          const conflict = await checkConflict.trigger({
            id_number: digits,
            exclude_person_id: row.person_id,
          }).result
          if (conflict?.conflict) {
            setMergePrompt({
              row,
              conflictPersonId: conflict.person_id,
              fullName: conflict.full_name,
            })
            return
          }
        }
        await doUpdate(row, draft)
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        toast.error('Could not update identity', { description: msg })
      } finally {
        setSavingId(null)
      }
    },
    [drafts, checkConflict, doUpdate],
  )

  const handleMerge = useCallback(async () => {
    if (!mergePrompt) return
    setMerging(true)
    try {
      await merge.trigger({
        keep_person_id: mergePrompt.row.person_id,
        merge_person_id: mergePrompt.conflictPersonId,
      }).result
      toast.success('Records merged')
      setMergePrompt(null)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not merge records', { description: msg })
    } finally {
      setMerging(false)
    }
  }, [mergePrompt, merge, load])

  const rowCount = useMemo(() => rows.length, [rows])

  const onboardingTrigger = onboarding.trigger
  const loadOnboarding = useCallback(() => {
    onboardingTrigger({}, { skipCache: true })
  }, [onboardingTrigger])

  useEffect(() => {
    if (view === 'onboarding') loadOnboarding()
  }, [view, loadOnboarding])

  const bankingTrigger = banking.trigger
  const loadBanking = useCallback(() => {
    bankingTrigger({}, { skipCache: true })
  }, [bankingTrigger])

  useEffect(() => {
    if (view === 'banking') loadBanking()
  }, [view, loadBanking])

  const bankingRows = (banking.data as BankFixupRow[] | undefined) ?? []
  // Filtered here rather than in the query, because the count above the table
  // has to keep saying how many there are in total. A filter that also changes
  // the denominator hides the size of the problem.
  const filteredBanking = useMemo(
    () => (bankingOnlyWorking ? bankingRows.filter((r) => r.shifts_last_60d > 0) : bankingRows),
    [bankingRows, bankingOnlyWorking],
  )

  const onboardingRows = (onboarding.data as OnboardingRow[] | undefined) ?? []
  const onboardingMatched = useMemo(
    () => onboardingRows.filter((r) => r.matched).length,
    [onboardingRows],
  )
  const filteredOnboarding = useMemo(() => {
    const q = onboardingFilter.trim().toLowerCase()
    if (!q) return onboardingRows
    return onboardingRows.filter(
      (r) =>
        r.sheet_name?.toLowerCase().includes(q) ||
        r.sheet_email?.toLowerCase().includes(q),
    )
  }, [onboardingRows, onboardingFilter])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <UserCog className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Data Fixes</h1>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {fixups.loading ? '…' : `${rowCount} to fix`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {caption || 'Correct team members with a malformed or missing South African ID number.'}
        </p>
      </div>

      {!canAct && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
          <Eye className="h-3.5 w-3.5 shrink-0" />
          You have read-only access.
        </div>
      )}

      {/* View toggle */}
      <div className="border-b border-border px-4 py-2.5">
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setView('ids')}
            className={
              view === 'ids'
                ? 'rounded-md bg-background px-3 py-1.5 font-medium text-foreground shadow-retool-sm'
                : 'rounded-md px-3 py-1.5 text-muted-foreground transition-colors hover:text-foreground'
            }
          >
            ID numbers
          </button>
          <button
            type="button"
            onClick={() => setView('onboarding')}
            className={
              view === 'onboarding'
                ? 'rounded-md bg-background px-3 py-1.5 font-medium text-foreground shadow-retool-sm'
                : 'rounded-md px-3 py-1.5 text-muted-foreground transition-colors hover:text-foreground'
            }
          >
            Onboarding form
          </button>
          <button
            type="button"
            onClick={() => setView('banking')}
            className={
              view === 'banking'
                ? 'rounded-md bg-background px-3 py-1.5 font-medium text-foreground shadow-retool-sm'
                : 'rounded-md px-3 py-1.5 text-muted-foreground transition-colors hover:text-foreground'
            }
          >
            Banking
          </button>
        </div>
      </div>

      {view === 'banking' ? (
        <BankingView
          loading={banking.loading}
          error={banking.error}
          rows={filteredBanking}
          onlyWorking={bankingOnlyWorking}
          onOnlyWorkingChange={setBankingOnlyWorking}
        />
      ) : view === 'onboarding' ? (
        <OnboardingView
          loading={onboarding.loading}
          error={onboarding.error}
          rows={filteredOnboarding}
          total={onboardingRows.length}
          matched={onboardingMatched}
          filter={onboardingFilter}
          onFilterChange={setOnboardingFilter}
        />
      ) : (
      /* Body */
      <div className="min-h-0 flex-1 overflow-auto">
        {fixups.loading ? (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : fixups.error ? (
          <div className="p-6 text-sm text-destructive">{fixups.error}</div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <UserCog className="h-6 w-6 opacity-60" />
            Nothing to fix. Every ID number looks valid.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Current ID number</TableHead>
                <TableHead>ID status</TableHead>
                <TableHead>Date of birth</TableHead>
                {canAct && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const draft = drafts[row.person_id] ?? { idNumber: '', dob: '' }
                const saving = savingId === row.person_id
                return (
                  <TableRow key={row.person_id}>
                    <TableCell className="font-medium text-foreground">
                      {row.first_name} {row.last_name}
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">
                      {row.role}
                    </TableCell>
                    <TableCell>
                      {canAct ? (
                        <Input
                          value={draft.idNumber}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [row.person_id]: { ...draft, idNumber: e.target.value },
                            }))
                          }
                          placeholder="13-digit ID number"
                          inputMode="numeric"
                          className="h-10 w-44 font-mono tabular-nums sm:h-9"
                          disabled={saving}
                        />
                      ) : (
                        <span className="font-mono tabular-nums text-muted-foreground">
                          {row.id_number ?? '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(row.id_status)}>{row.id_status}</Badge>
                    </TableCell>
                    <TableCell>
                      {canAct ? (
                        <Input
                          type="date"
                          value={draft.dob}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [row.person_id]: { ...draft, dob: e.target.value },
                            }))
                          }
                          className="h-10 w-40 sm:h-9"
                          disabled={saving}
                        />
                      ) : (
                        <span className="text-muted-foreground">{row.date_of_birth ?? '—'}</span>
                      )}
                    </TableCell>
                    {canAct && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => handleSave(row)}
                          disabled={saving}
                          className="h-10 sm:h-8"
                        >
                          {saving ? <Spinner className="h-4 w-4" /> : 'Save'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
      )}

      <AlertDialog
        open={mergePrompt !== null}
        onOpenChange={(open) => {
          if (!open && !merging) setMergePrompt(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge duplicate records?</AlertDialogTitle>
            <AlertDialogDescription>
              This ID number already belongs to {mergePrompt?.fullName}. Merging will combine
              both records into this one and remove the duplicate. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleMerge()
              }}
              disabled={merging}
            >
              {merging ? <Spinner className="h-4 w-4" /> : 'Merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function OnboardingView({
  loading,
  error,
  rows,
  total,
  matched,
  filter,
  onFilterChange,
}: {
  loading: boolean
  error: string | null
  rows: OnboardingRow[]
  total: number
  matched: number
  filter: string
  onFilterChange: (value: string) => void
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2.5 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">Onboarding form</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {loading ? '\u2026' : `${matched}/${total} matched`}
          </span>
          <Input
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Filter by name or email"
            className="h-10 w-full sm:ml-auto sm:h-9 sm:w-64"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Everyone who completed the onboarding form. Unmatched rows could not be linked to a
          pipeline applicant and may need a manual link.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading\u2026
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-destructive">{error}</div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <FileText className="h-6 w-6 opacity-60" />
            No onboarding submissions match your filter.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Match</TableHead>
                <TableHead>Pipeline</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium text-foreground">{row.sheet_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono tabular-nums">{row.sheet_id}</span>
                      {row.id_length_flag && (
                        <Badge variant="destructive">check ID</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.sheet_email}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.submitted_at_raw ?? '\u2014'}
                  </TableCell>
                  <TableCell>
                    {row.matched ? (
                      <Badge variant="default">Matched: {row.match_basis}</Badge>
                    ) : (
                      <Badge variant="destructive">Unmatched</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {humanisePipeline(row.pipeline_status)}
                  </TableCell>
                  <TableCell>
                    {row.id_mismatch_flag && (
                      <Badge variant="warning">ID differs from record</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- banking view

type BankFixupRow = {
  driver_id: string
  person_id: string
  driver_name: string
  id_number: string | null
  primary_phone: string | null
  shifts_last_60d: number
  last_shift: string | null
  has_letter_backed_account: boolean
  in_simplepay: boolean
  simplepay_holds_an_account: boolean
  bank_key: string | null
  holder_matches_driver: boolean | null
  letters_awaiting_extraction: number
  letters_awaiting_verification: number
  issue: string
  severity: number
}

// Severity 1 and 2 are "cannot be paid at all". 3 and 4 are "paid into an
// account with no letter behind it". The split is the database's, not the
// screen's, so a change to what counts as serious is one migration and not a
// deploy.
function severityTone(severity: number): 'destructive' | 'warning' | 'secondary' {
  if (severity === 1) return 'destructive'
  if (severity === 2) return 'warning'
  return 'secondary'
}

function severityLabel(severity: number): string {
  if (severity === 1) return 'Cannot be paid, working now'
  if (severity === 2) return 'Cannot be paid'
  if (severity === 3) return 'No letter, working now'
  return 'No letter'
}

function BankingView({
  loading,
  error,
  rows,
  onlyWorking,
  onOnlyWorkingChange,
}: {
  loading: boolean
  error: unknown
  rows: BankFixupRow[]
  onlyWorking: boolean
  onOnlyWorkingChange: (v: boolean) => void
}) {
  const cannotBePaid = rows.filter((r) => r.severity <= 2).length
  const working = rows.filter((r) => r.shifts_last_60d > 0).length

  // A failed load has no counts, so it must not print any. On 25 August this bar
  // read "0 drivers, 0 cannot be paid" directly above the words "could not be
  // loaded", which is a screen telling the operator two contradictory things and
  // inviting them to believe the reassuring one. Rule 6: a missing figure shows
  // as missing, never as zero. The same applies while the first load is still in
  // flight, because zero is an answer and "not yet" is not.
  const countsAreReal = !error && !(loading && rows.length === 0)

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 text-xs text-muted-foreground">
        {countsAreReal ? (
          <>
            <span>
              <span className="font-medium text-foreground">{rows.length}</span> drivers
            </span>
            <span>
              <span className="font-medium text-foreground">{cannotBePaid}</span> cannot be paid
            </span>
            <span>
              <span className="font-medium text-foreground">{working}</span> worked in the last 60
              days
            </span>
          </>
        ) : (
          <span>{error ? 'Counts unavailable' : 'Counting…'}</span>
        )}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyWorking}
            onChange={(e) => onOnlyWorkingChange(e.target.checked)}
            className="h-3.5 w-3.5 accent-current"
            disabled={!countsAreReal}
          />
          Only drivers who worked in the last 60 days
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading && rows.length === 0 ? (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : error ? (
          <p className="px-4 py-6 text-sm text-destructive">
            The banking list could not be loaded. Try again, and if it keeps failing say so
            rather than working around it.
          </p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            Every driver has a bank account backed by a confirmation letter. Nothing to fix.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Shifts, 60d</TableHead>
                  <TableHead>Last shift</TableHead>
                  <TableHead>What is wrong</TableHead>
                  <TableHead>Waiting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.driver_id}>
                    <TableCell className="whitespace-nowrap font-medium">
                      {r.driver_name}
                      {r.primary_phone && (
                        <span className="ml-2 font-normal text-muted-foreground">
                          {r.primary_phone}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={severityTone(r.severity)}>{severityLabel(r.severity)}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.shifts_last_60d}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {r.last_shift ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.issue}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {r.letters_awaiting_verification > 0
                        ? `${r.letters_awaiting_verification} letter to confirm`
                        : r.letters_awaiting_extraction > 0
                          ? `${r.letters_awaiting_extraction} letter being read`
                          : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  )
}
